import { lazy, Suspense, useCallback } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { ConversationsList, If, ImagePreviewPanel, NoConversationMsg } from ".."
import ConversationsThread from "../conversations-thread"
import ConversationLeadDetailsPanel from "../conversation-lead-details-panel"
import PillTabs from "../pill-tabs"
import { Threads } from "../../../types"
import {
  ConversationsListSkeleton,
  ConversationsThreadSkeleton
} from "../skeletons"

const AdvisorConversationsList = lazy(
  () => import("../advisor-conversations-list")
)
const AdvisorThread = lazy(() => import("../advisor-thread"))
const WebsiteChatConversationsList = lazy(
  () => import("../website-chat-conversations-list")
)
const WebsiteChatThread = lazy(() => import("../website-chat-thread"))

type ViewTab = "sms" | "advisor" | "website-chat"

const VIEW_TABS = [
  { id: "sms" as const, label: "SMS" },
  { id: "advisor" as const, label: "AI Advisor" },
  { id: "website-chat" as const, label: "Website Chat" }
]

interface Props {
  isLoading: boolean
  threads: Threads
  filteredThreads: Threads
  hasMore: boolean
  loadMore: () => void
  isLoadingMore: boolean
  hasNoSmsThreads: boolean
}

const ConversationsWithListAndThread = ({
  isLoading,
  threads,
  filteredThreads,
  hasMore,
  loadMore,
  isLoadingMore,
  hasNoSmsThreads
}: Props) => {
  const { _id = "", sessionId = "" } = useParams<{
    _id: string
    sessionId: string
  }>()
  const navigate = useNavigate()
  const location = useLocation()

  const activeView: ViewTab = location.pathname.startsWith(
    "/conversations/website-chat"
  )
    ? "website-chat"
    : location.pathname.startsWith("/conversations/advisor")
    ? "advisor"
    : "sms"

  const handleViewChange = useCallback(
    (tab: string) => {
      if (tab === "sms") navigate("/conversations/sms")
      else if (tab === "advisor") navigate("/conversations/advisor")
      else navigate("/conversations/website-chat")
    },
    [navigate]
  )

  const handleSelectAdvisorSession = useCallback(
    (id: string) => navigate(`/conversations/advisor/${id}`),
    [navigate]
  )

  const handleSelectWebsiteChatSession = useCallback(
    (id: string) => navigate(`/conversations/website-chat/${id}`),
    [navigate]
  )

  const isSms = activeView === "sms"
  const isAdvisor = activeView === "advisor"
  const isWebsiteChat = activeView === "website-chat"

  return (
    <>
      <div className="flex flex-1 overflow-hidden h-full">
        {/* Sidebar: tabs + list */}
        <div className="w-screen sm:w-[367px] min-w-full sm:min-w-[367px] sm:max-w-[367px] flex flex-col bg-white border-r border-neutral-200">
          <div className="shrink-0 border-b border-neutral-200 px-4 pt-3 pb-2">
            <PillTabs
              tabs={VIEW_TABS}
              currentTab={activeView}
              onChange={handleViewChange}
              className="justify-center"
            />
          </div>

          <div className="flex-1 flex flex-col min-h-0">
            {isSms ? (
              <ConversationsList
                filteredThreads={filteredThreads}
                isLoading={isLoading}
                hasMore={hasMore}
                loadMore={loadMore}
                isLoadingMore={isLoadingMore}
              />
            ) : isAdvisor ? (
              <Suspense fallback={<ConversationsListSkeleton />}>
                <AdvisorConversationsList
                  selectedSessionId={sessionId}
                  onSelectSession={handleSelectAdvisorSession}
                />
              </Suspense>
            ) : (
              <Suspense fallback={<ConversationsListSkeleton />}>
                <WebsiteChatConversationsList
                  selectedSessionId={sessionId}
                  onSelectSession={handleSelectWebsiteChatSession}
                />
              </Suspense>
            )}
          </div>
        </div>

        {/* Right panel */}
        {isSms ? (
          hasNoSmsThreads ? (
            <NoConversationMsg />
          ) : _id ? (
            <div className="bg-[#F8F8F8] flex flex-col overflow-y-auto min-w-full w-screen sm:w-full transition-transform sm:min-w-0 z-20 -translate-x-full sm:transform-none">
              <ConversationsThread threads={threads} />
            </div>
          ) : (
            <If
              condition={!isLoading}
              then={
                <div className="bg-[#F8F8F8] flex-col items-center justify-center w-full hidden md:flex">
                  <p>Select a thread to view messages.</p>
                </div>
              }
            />
          )
        ) : isAdvisor ? (
          <div
            className={`bg-[#F8F8F8] flex flex-col overflow-y-auto min-w-full w-screen sm:w-full transition-transform sm:min-w-0 z-20 sm:transform-none ${
              sessionId ? "-translate-x-full" : ""
            }`}
          >
            <Suspense fallback={<ConversationsThreadSkeleton />}>
              <AdvisorThread sessionId={sessionId} />
            </Suspense>
          </div>
        ) : (
          <div
            className={`bg-[#F8F8F8] flex flex-col overflow-y-auto min-w-full w-screen sm:w-full transition-transform sm:min-w-0 z-20 sm:transform-none ${
              sessionId ? "-translate-x-full" : ""
            }`}
          >
            <Suspense fallback={<ConversationsThreadSkeleton />}>
              <WebsiteChatThread sessionId={sessionId} />
            </Suspense>
          </div>
        )}
      </div>
      <ImagePreviewPanel />
      {!isWebsiteChat && <ConversationLeadDetailsPanel />}
    </>
  )
}

export default ConversationsWithListAndThread
